import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import React, { useEffect, useRef, useState } from 'react'
import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import classNames from 'classnames'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import type { CalendarEvent } from 'paintscout'
import { getCrews } from '@paintscout/util/builder/options'
import moment from 'moment'
import ProductionEventPopper from './ProductionEventPopper'
import { getProjectTypeLabel } from './util'
import { useProductionCalendar } from './ProductionCalendarContext'
import type { ZoomLevel } from './TimelineHeader'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      height: '3rem', // h-12
      cursor: 'pointer',
      transition: 'all 0.2s',
      position: 'relative',
      overflow: 'visible'
    },
    jobBlockContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'visible'
    },
    jobBlock: {
      height: '100%',
      borderRadius: theme.shape.borderRadius,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      overflow: 'visible'
    },
    labelContainer: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      paddingTop: theme.spacing(0.5),
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(1),
      flex: 1,
      position: 'relative',
      zIndex: 1500
    },
    labelSingleDay: {
      overflow: 'hidden',
      paddingRight: '2px'
    },
    labelMultiDay: {
      minWidth: 0,
      // overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start'
    },
    labelInnerContainer: {
      position: 'sticky',
      left: theme.spacing(2),
      backgroundColor: 'transparent'
    },
    innerSingleDay: {
      overflow: 'hidden',
      maxWidth: '100%',
      left: 0
    },
    innerMultiDay: {
      flexShrink: 0
    },
    singleDayText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    multiDayText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    typeText: {
      textTransform: 'capitalize'
    },
    checkIcon: {
      width: 14,
      height: 14
    },
    progressBarContainer: {
      width: '100%',
      height: 4
    },
    progressBar: {
      height: '100%',
      transition: 'all 0.3s ease'
    },
    leftResizeHandle: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: theme.spacing(1.5),
      cursor: 'ew-resize',
      zIndex: 2000
    },
    rightResizeHandle: {
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: theme.spacing(1.5),
      cursor: 'ew-resize',
      zIndex: 2000
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5)
    }
  }),
  { name: 'ProjectBlock' }
)

export interface ProjectBlockProps {
  classes?: StyleClasses<typeof useStyles>
  event?: CalendarEvent
  onHover: (eventId: string | null) => void
  isHovered: boolean
  color: string // Kept for backwards compatibility but now using status color
  onEventUpdate?: (updatedEvent: CalendarEvent) => void
  cellWidth: number // Width of each date cell in pixels
  zoomLevel?: ZoomLevel // Optional zoom level to control text visibility
}

function ProjectBlock(props: ProjectBlockProps) {
  const classes = useStyles(props)
  const { event, onHover, isHovered, onEventUpdate, cellWidth, zoomLevel = 'days' } = props
  const { options } = useClientOptions()
  const { events, setEvents } = useProductionCalendar()
  const crews = getCrews({ options, includeTrashed: true })
  const [showTooltip, setShowTooltip] = useState(false)
  const [isClicked, setIsClicked] = useState(false)
  const [_tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [isResizing, setIsResizing] = useState(false)
  const blockRef = useRef<HTMLDivElement>(null)
  const [_isMobile, setIsMobile] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null)
  const tempEvent = events.find((ev) => ev.id === event.id)

  // Check the URL and if eventId is in there we will set it as outlined until we reset it
  const urlParams = new URLSearchParams(window.location.search)
  const [highlightEventId, setHighlightEventId] = useState(urlParams.get('eventId') === event.id)
  if (highlightEventId) {
    urlParams.delete('eventId')
    const newUrl =
      window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '') + window.location.hash
    window.history.replaceState({}, '', newUrl)
  }

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'event',
    item: { eventId: event.id, event },
    canDrag: () => !isResizing,
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  // Suppress the native browser drag ghost (the Safari globe icon) by replacing
  // it with a transparent 1×1 image. CustomDragLayer renders the actual preview.
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true })
  }, [preview])

  const isComplete = event.progress === 100

  // Use crew-based color instead of status color
  const crewColor = crews.find((c) => c._id === event.crew).color

  const handleMouseEnter = (_e: React.MouseEvent) => {
    onHover(event.id)
    if (blockRef.current) {
      const rect = blockRef.current.getBoundingClientRect()
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top
      })
    }
  }

  const handleMouseLeave = () => {
    onHover(null)
  }

  // Resize handlers for entire event
  const handleResizeStart = (e: React.MouseEvent, side: 'left' | 'right') => {
    e.stopPropagation()
    e.preventDefault()
    setIsResizing(true)

    const startX = e.clientX
    const currentStart = new Date(event.start!)
    const currentEnd = new Date(event.end!)

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault()
      moveEvent.stopPropagation()

      const deltaX = moveEvent.clientX - startX
      // Calculate how many cells (dates) we've moved
      const cellsDelta = Math.round(deltaX / cellWidth)

      if (cellsDelta === 0) return

      if (side === 'left') {
        const newStart = new Date(currentStart)
        newStart.setDate(newStart.getDate() + cellsDelta)

        // Ensure new start is before or on the same day as current end (minimum 1 day duration)
        if (newStart <= currentEnd) {
          const newEvents = events.map((ev) => {
            if (ev.id === event.id) {
              return {
                ...ev,
                start: newStart
              }
            }
            return ev
          })
          tempEvent.start = newStart
          setEvents(newEvents)
        }
      } else {
        const newEnd = new Date(currentEnd)
        newEnd.setDate(newEnd.getDate() + cellsDelta)

        // Ensure new end is on the same day or after current start (minimum 1 day duration)
        if (newEnd >= currentStart) {
          const newEvents = events.map((ev) => {
            if (ev.id === event.id) {
              return {
                ...ev,
                end: newEnd
              }
            }
            return ev
          })
          tempEvent.end = newEnd
          setEvents(newEvents)
        }
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      if (onEventUpdate) {
        onEventUpdate(tempEvent)
      }
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  useEffect(() => {
    const handleScroll = () => {
      if ((showTooltip || isClicked) && blockRef.current) {
        const rect = blockRef.current.getBoundingClientRect()
        setTooltipPosition({
          x: rect.left + rect.width / 2,
          y: rect.top
        })
      }
    }

    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [showTooltip, isClicked])

  const handleMarkerClick = (event: CalendarEvent, clickEvent: React.MouseEvent) => {
    setHighlightEventId(null)
    clickEvent.stopPropagation()
    setIsClicked(!isClicked)
    setShowTooltip(!isClicked)
    setSelectedEvent(event)
    setAnchorPosition({
      top: clickEvent.clientY,
      left: clickEvent.clientX
    })
  }

  // Show text labels only in days view
  const showLabels = zoomLevel === 'days'

  // Check if job is only one day
  const isSingleDay = event.start && event.end && moment(event.start).isSame(moment(event.end), 'day')

  return (
    <>
      <div
        ref={drag}
        style={{
          backgroundColor: 'transparent',
          opacity: isDragging ? 0 : 1,
          zIndex: isClicked ? 20 : isHovered ? 10 : 1,
          border: highlightEventId ? `2px solid ${crewColor}` : 'none',
          borderRadius: highlightEventId ? 4 : 0
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => (isClicked ? null : handleMarkerClick(event, e))}
        className={classes.root}
      >
        {/* Single job block */}
        <div className={classes.jobBlockContainer}>
          <div
            className={classes.jobBlock}
            style={{
              backgroundColor: crewColor + (isHovered || isClicked ? '40' : '30'),
              boxShadow: isClicked
                ? `0 0 0 3px rgba(255, 255, 255, 0.9), 0 0 0 5px ${crewColor}, 0 6px 20px rgba(0, 0, 0, 0.25)`
                : isHovered
                  ? '0 4px 12px rgba(0, 0, 0, 0.15)'
                  : '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Client name and event type - only show in days view */}

            {showLabels && (
              <div
                className={classNames({
                  [classes.labelContainer]: true,
                  [isSingleDay ? classes.labelSingleDay : classes.labelMultiDay]: true
                })}
              >
                <div
                  className={classNames({
                    [classes.labelInnerContainer]: true,
                    [isSingleDay ? classes.innerSingleDay : classes.innerMultiDay]: true
                  })}
                >
                  <div
                    className={classNames({ [isSingleDay ? classes.singleDayText : classes.multiDayText]: true })}
                    // style={{ color: '#1F2E47', fontWeight: 600 }}
                  >
                    <Typography variant={'h6'} color="textPrimary" weight={'bold'} className={classes.title}>
                      {/* {event.contact.firstName} {event.contact.lastName} */}
                      {event.deal.name}
                      {isComplete && !isSingleDay && (
                        <CheckCircleIcon className={classes.checkIcon} style={{ color: crewColor }} />
                      )}
                    </Typography>
                  </div>
                  <div
                    className={classNames({
                      [classes.typeText]: true,
                      [isSingleDay ? classes.singleDayText : classes.multiDayText]: true
                    })}
                  >
                    <Typography variant="subtitle1">{getProjectTypeLabel(event.projectType, options)}</Typography>
                  </div>
                </div>
              </div>
            )}

            {/* Progress bar */}
            <div className={classes.progressBarContainer} style={{ backgroundColor: `${crewColor}40` }}>
              <div
                className={classes.progressBar}
                style={{
                  width: `${event.progress}%`,
                  backgroundColor: crewColor
                }}
              />
            </div>
          </div>
        </div>

        {/* Resize handles - invisible areas with cursor change only */}
        {(isHovered || isClicked) && !isDragging && (
          <>
            {/* Left resize handle */}
            <div
              className={classes.leftResizeHandle}
              style={{ pointerEvents: 'auto' }}
              onMouseDown={(e) => handleResizeStart(e, 'left')}
            />

            {/* Right resize handle */}
            <div
              className={classes.rightResizeHandle}
              style={{ pointerEvents: 'auto' }}
              onMouseDown={(e) => handleResizeStart(e, 'right')}
            />
          </>
        )}
      </div>
      {/* {showTooltip && !isDragging && projectManager && (
        <ProjectTooltip
          event={event}
          crew={crews.find((c) => c._id === event.crew)!}
          position={tooltipPosition}
          onEventUpdate={onEventUpdate}
          onEventDelete={onEventDelete}
          onClose={handleCloseTooltip}
          isMobile={isMobile}
        />
      )} */}
      {showTooltip && !isDragging && selectedEvent && anchorPosition && (
        <ProductionEventPopper
          event={selectedEvent}
          anchorEl={
            {
              getBoundingClientRect: () => ({
                top: anchorPosition.top,
                left: anchorPosition.left,
                right: anchorPosition.left,
                bottom: anchorPosition.top,
                width: 0,
                height: 0,
                x: anchorPosition.left,
                y: anchorPosition.top,
                toJSON: () => ({
                  top: anchorPosition.top,
                  left: anchorPosition.left,
                  right: anchorPosition.left,
                  bottom: anchorPosition.top,
                  width: 0,
                  height: 0,
                  x: anchorPosition.left,
                  y: anchorPosition.top
                })
              }),
              clientWidth: 0,
              clientHeight: 0
            } as unknown as HTMLElement
          } // Use a virtual element for positioning
          onClose={() => {
            setSelectedEvent(null)
            setAnchorPosition(null)
            setShowTooltip(false)
            setIsClicked(false)
          }}
          closeOnEdit
        />
      )}
    </>
  )
}

export default ProjectBlock
